/**
 * CampaignMessageBuilder - Construtor de sequência de mensagens (layout Studio com Timeline)
 *
 * Layout:
 * - Dock lateral esquerdo (80px) com 10 tipos como botões-ícone + tooltip
 * - Canvas direito com spine vertical e itens em timeline (renderizados por MessageItemCard)
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Type,
  Image,
  Video,
  Music,
  FileText,
  Timer,
  Circle,
  Film,
  Plus,
  MousePointerClick,
  User,
} from 'lucide-react';
import { MessageItem, CampaignItemType, ButtonsConfig } from './types';
import { MessageItemCard } from './MessageItemCard';
import { AddTextItemDialog } from './AddTextItemDialog';
import { AddMediaItemDialog } from './AddMediaItemDialog';
import { AddDelayItemDialog } from './AddDelayItemDialog';
import { AddImageButtonsItemDialog } from './AddImageButtonsItemDialog';
import { AddContactItemDialog } from './AddContactItemDialog';
import { createLogger } from '@/lib/logger';

const logger = createLogger('CampaignMessageBuilder');

// =====================================================
// INTERFACES
// =====================================================

interface CampaignMessageBuilderProps {
  items: MessageItem[];
  onItemsChange: (items: MessageItem[]) => void;
  tenantId?: string;
  /** Variáveis disponíveis da lista de campanha (ex: ['variavel1', 'variavel2']) */
  availableVariables?: string[];
}

// Configuração dos tipos de item — cada um com cor de hover própria (consistente com MessageItemCard)
const itemTypes: {
  type: CampaignItemType;
  label: string;
  icon: React.ElementType;
  hover: string;
}[] = [
  { type: 'text',          label: 'Texto',         icon: Type,               hover: 'hover:text-indigo-400 hover:border-indigo-500/50' },
  { type: 'image',         label: 'Imagem',        icon: Image,              hover: 'hover:text-emerald-400 hover:border-emerald-500/50' },
  { type: 'video',         label: 'Vídeo',         icon: Video,              hover: 'hover:text-amber-400 hover:border-amber-500/50' },
  { type: 'audio',         label: 'Áudio',         icon: Music,              hover: 'hover:text-orange-400 hover:border-orange-500/50' },
  { type: 'ptv',           label: 'PTV',           icon: Circle,             hover: 'hover:text-pink-400 hover:border-pink-500/50' },
  { type: 'gif',           label: 'GIF',           icon: Film,               hover: 'hover:text-cyan-400 hover:border-cyan-500/50' },
  { type: 'document',      label: 'Documento',     icon: FileText,           hover: 'hover:text-yellow-400 hover:border-yellow-500/50' },
  { type: 'delay',         label: 'Delay',         icon: Timer,              hover: 'hover:text-zinc-200 hover:border-zinc-500/50' },
  { type: 'image_buttons', label: 'Img c/ Botões', icon: MousePointerClick,  hover: 'hover:text-violet-400 hover:border-violet-500/50' },
  { type: 'contact',       label: 'Contato',       icon: User,               hover: 'hover:text-lime-400 hover:border-lime-500/50' },
];

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function CampaignMessageBuilder({
  items,
  onItemsChange,
  tenantId,
  availableVariables = [],
}: CampaignMessageBuilderProps) {
  // Estado para dialogs
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [delayDialogOpen, setDelayDialogOpen] = useState(false);
  const [imageButtonsDialogOpen, setImageButtonsDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedMediaType, setSelectedMediaType] = useState<CampaignItemType>('image');

  // Estado para edição
  const [editingItem, setEditingItem] = useState<MessageItem | null>(null);

  // Estado para drag & drop
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // =====================================================
  // HANDLERS
  // =====================================================

  /**
   * Adiciona novo item à lista — roteia para o dialog correto baseado no tipo
   */
  const handleAddItem = (type: CampaignItemType) => {
    logger.debug('Adicionando item', { type });

    if (type === 'text') {
      setEditingItem(null);
      setTextDialogOpen(true);
    } else if (type === 'delay') {
      setEditingItem(null);
      setDelayDialogOpen(true);
    } else if (type === 'image_buttons') {
      setEditingItem(null);
      setImageButtonsDialogOpen(true);
    } else if (type === 'contact') {
      setEditingItem(null);
      setContactDialogOpen(true);
    } else {
      setSelectedMediaType(type);
      setEditingItem(null);
      setMediaDialogOpen(true);
    }
  };

  /** Salva item de texto */
  const handleSaveTextItem = (textContent: string) => {
    const newItem: MessageItem = {
      id: editingItem?.id || crypto.randomUUID(),
      item_type: 'text',
      sort_order: editingItem ? editingItem.sort_order : items.length,
      text_content: textContent,
    };

    if (editingItem) {
      onItemsChange(items.map(item => (item.id === editingItem.id ? newItem : item)));
    } else {
      onItemsChange([...items, newItem]);
    }

    setTextDialogOpen(false);
    setEditingItem(null);
  };

  /** Salva item de mídia */
  const handleSaveMediaItem = (data: {
    mediaUrl: string;
    mediaFilename?: string;
    mediaMimeType?: string;
    caption?: string;
  }) => {
    const newItem: MessageItem = {
      id: editingItem?.id || crypto.randomUUID(),
      item_type: selectedMediaType,
      sort_order: editingItem ? editingItem.sort_order : items.length,
      text_content: data.caption,
      media_url: data.mediaUrl,
      media_filename: data.mediaFilename,
      media_mime_type: data.mediaMimeType,
    };

    if (editingItem) {
      onItemsChange(items.map(item => (item.id === editingItem.id ? newItem : item)));
    } else {
      onItemsChange([...items, newItem]);
    }

    setMediaDialogOpen(false);
    setEditingItem(null);
  };

  /** Salva item de delay */
  const handleSaveDelayItem = (seconds: number) => {
    const newItem: MessageItem = {
      id: editingItem?.id || crypto.randomUUID(),
      item_type: 'delay',
      sort_order: editingItem ? editingItem.sort_order : items.length,
      delay_seconds: seconds,
    };

    if (editingItem) {
      onItemsChange(items.map(item => (item.id === editingItem.id ? newItem : item)));
    } else {
      onItemsChange([...items, newItem]);
    }

    setDelayDialogOpen(false);
    setEditingItem(null);
  };

  /** Salva item de imagem com botões */
  const handleSaveImageButtonsItem = (data: {
    mediaUrl: string;
    mediaFilename?: string;
    mediaMimeType?: string;
    textContent: string;
    buttonsConfig: ButtonsConfig;
  }) => {
    const newItem: MessageItem = {
      id: editingItem?.id || crypto.randomUUID(),
      item_type: 'image_buttons',
      sort_order: editingItem ? editingItem.sort_order : items.length,
      text_content: data.textContent,
      media_url: data.mediaUrl,
      media_filename: data.mediaFilename,
      media_mime_type: data.mediaMimeType,
      buttons_config: data.buttonsConfig,
    };

    if (editingItem) {
      onItemsChange(items.map(item => (item.id === editingItem.id ? newItem : item)));
    } else {
      onItemsChange([...items, newItem]);
    }

    setImageButtonsDialogOpen(false);
    setEditingItem(null);
  };

  /** Salva item de contato (vCard) */
  const handleSaveContactItem = (data: {
    contactName: string;
    contactPhone: string;
    businessDescription?: string;
  }) => {
    const newItem: MessageItem = {
      id: editingItem?.id || crypto.randomUUID(),
      item_type: 'contact',
      sort_order: editingItem ? editingItem.sort_order : items.length,
      contact_name: data.contactName,
      contact_phone: data.contactPhone,
      contact_business_description: data.businessDescription,
    };

    if (editingItem) {
      onItemsChange(items.map(item => (item.id === editingItem.id ? newItem : item)));
    } else {
      onItemsChange([...items, newItem]);
    }

    setContactDialogOpen(false);
    setEditingItem(null);
  };

  /** Edita item existente */
  const handleEditItem = (item: MessageItem) => {
    setEditingItem(item);

    if (item.item_type === 'text') {
      setTextDialogOpen(true);
    } else if (item.item_type === 'delay') {
      setDelayDialogOpen(true);
    } else if (item.item_type === 'image_buttons') {
      setImageButtonsDialogOpen(true);
    } else if (item.item_type === 'contact') {
      setContactDialogOpen(true);
    } else {
      setSelectedMediaType(item.item_type);
      setMediaDialogOpen(true);
    }
  };

  /** Remove item da lista */
  const handleRemoveItem = (itemId: string) => {
    logger.debug('Removendo item', { itemId });
    const newItems = items.filter(item => item.id !== itemId);
    const reorderedItems = newItems.map((item, index) => ({
      ...item,
      sort_order: index,
    }));
    onItemsChange(reorderedItems);
  };

  /** Drag & drop */
  const handleDragStart = (index: number) => setDraggedIndex(index);
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);

    const reorderedItems = newItems.map((item, idx) => ({ ...item, sort_order: idx }));
    onItemsChange(reorderedItems);
    setDraggedIndex(index);
  };
  const handleDragEnd = () => setDraggedIndex(null);

  // =====================================================
  // RENDER — layout Studio (dock + timeline)
  // =====================================================

  return (
    <div className="flex rounded-xl overflow-hidden border border-zinc-200 bg-white">
      {/* ====== DOCK LATERAL ESQUERDO (paleta de tipos) ====== */}
      <div className="w-20 bg-zinc-50 border-r border-zinc-200 p-3 flex flex-col gap-2 items-center shrink-0">
        <div className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">
          Compor
        </div>

        <TooltipProvider delayDuration={200}>
          {itemTypes.map(({ type, label, icon: Icon, hover }) => (
            <Tooltip key={type}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => handleAddItem(type)}
                  className={`p-2.5 bg-white text-zinc-500 rounded-xl border border-zinc-200 transition-all shadow-sm ${hover}`}
                  aria-label={label}
                >
                  <Icon className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-zinc-900 text-zinc-100 border-zinc-800 text-xs">
                {label}
              </TooltipContent>
            </Tooltip>
          ))}
        </TooltipProvider>
      </div>

      {/* ====== CANVAS PRINCIPAL (timeline com spine) ====== */}
      <div className="flex-1 min-w-0 bg-zinc-50/50 relative">
        <ScrollArea className="h-[320px] sm:h-[380px] md:h-[440px] lg:h-[480px]">
          <div className="p-8 relative">
            {items.length === 0 ? (
              // Estado vazio
              <div className="flex flex-col items-center justify-center py-16 text-zinc-400">
                <div className="p-4 rounded-full bg-zinc-50 border border-zinc-200 mb-4">
                  <Plus className="h-8 w-8 opacity-60" />
                </div>
                <p className="text-sm font-medium text-zinc-500">
                  Sua timeline está vazia
                </p>
                <p className="text-xs text-zinc-400 mt-1.5 text-center max-w-xs">
                  Use a paleta à esquerda para adicionar blocos à sequência de mensagens
                </p>
              </div>
            ) : (
              <>
                {/* Spine vertical conectando os nós (alinhada ao centro do nó w-8 + pl-8) */}
                <div className="absolute left-[47px] top-8 bottom-12 w-px bg-zinc-300" aria-hidden="true" />

                {/* Itens da timeline */}
                <div className="space-y-5 relative">
                  {items
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((item, index) => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`transition-opacity ${
                          draggedIndex === index ? 'opacity-40' : 'opacity-100'
                        }`}
                      >
                        <MessageItemCard
                          item={item}
                          index={index}
                          onEdit={() => handleEditItem(item)}
                          onRemove={() => handleRemoveItem(item.id)}
                          tenantId={tenantId}
                        />
                      </div>
                    ))}

                  {/* Placeholder "Próximo slot" */}
                  <div className="relative flex items-center gap-6 opacity-40">
                    <div className="z-10 w-8 h-8 rounded-full bg-white border-2 border-dashed border-zinc-300 flex items-center justify-center text-[10px] font-bold text-zinc-400 shrink-0">
                      {String(items.length + 1).padStart(2, '0')}
                    </div>
                    <div className="flex-1 h-px border-t border-dashed border-zinc-300" />
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                      Próximo bloco
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer de status */}
        {items.length > 0 && (
          <div className="bg-zinc-50 px-6 py-2 border-t border-zinc-200 flex justify-between items-center">
            <div className="flex items-center gap-4 text-[10px] text-zinc-400 uppercase tracking-wider">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                Sequência ativa
              </span>
            </div>
            <div className="text-[10px] text-zinc-400 font-mono uppercase">
              {items.length} {items.length === 1 ? 'bloco' : 'blocos'}
            </div>
          </div>
        )}
      </div>

      {/* Dialogs (mantidos como estavam) */}
      <AddTextItemDialog
        open={textDialogOpen}
        onOpenChange={setTextDialogOpen}
        onSave={handleSaveTextItem}
        initialValue={editingItem?.text_content}
        availableVariables={availableVariables}
      />

      <AddMediaItemDialog
        open={mediaDialogOpen}
        onOpenChange={setMediaDialogOpen}
        onSave={handleSaveMediaItem}
        mediaType={selectedMediaType}
        initialValue={editingItem}
        tenantId={tenantId}
      />

      <AddDelayItemDialog
        open={delayDialogOpen}
        onOpenChange={setDelayDialogOpen}
        onSave={handleSaveDelayItem}
        initialValue={editingItem?.delay_seconds}
      />

      <AddImageButtonsItemDialog
        open={imageButtonsDialogOpen}
        onOpenChange={setImageButtonsDialogOpen}
        onSave={handleSaveImageButtonsItem}
        initialValue={editingItem}
        tenantId={tenantId}
      />

      <AddContactItemDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        onSave={handleSaveContactItem}
        initialValue={editingItem}
        tenantId={tenantId}
      />
    </div>
  );
}
